import { t } from "@lingui/macro";
import { Button, Input, Label } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { CheckCircle2 } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useState } from "react";

import { type personalInfoType, useAudioChatStore } from "@/client/stores/audioChatStore";

import {
  recordLegacyEvent,
  recordLegacyMessage,
  recordLegacyMessages,
  recordLegacyStateUpdate,
} from "./_utils/legacyTranscriptBridge";
import { TypingText } from "./typingText";

type Message = {
  type: "user" | "ai";
  content: string;
};
type messageType = Message[];
type PersonalInfoField = keyof personalInfoType;
type PersonalInfoErrors = Partial<Record<PersonalInfoField, string>>;
type TouchedPersonalInfoFields = Partial<Record<PersonalInfoField, boolean>>;
type PersonalInfoFieldConfig = {
  field: PersonalInfoField;
  label: string;
  placeholder: string;
  type: string;
  autoComplete: string;
  isRequired?: boolean;
};

const personalInfoPromptId = "personal-info-form";

const emptyPersonalInfo: personalInfoType = {
  name: "",
  phone: "",
  email: "",
  linkedin: "",
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeLinkedInUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const isValidLinkedInUrl = (value: string) => {
  const normalizedUrl = normalizeLinkedInUrl(value);
  if (!normalizedUrl) return true;

  try {
    const url = new URL(normalizedUrl);
    const hostname = url.hostname.toLowerCase();
    return hostname === "linkedin.com" || hostname.endsWith(".linkedin.com");
  } catch {
    return false;
  }
};

const getFirstName = (name: string) => name.trim().split(/\s+/)[0] || "there";

export const PersonalInfoChat = () => {
  const [showPerInfo, setShowPerInfo] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [messages, setMessages] = useState<messageType>([]);
  const [formValues, setFormValues] = useState<personalInfoType>(emptyPersonalInfo);
  const [formErrors, setFormErrors] = useState<PersonalInfoErrors>({});
  const [touchedFields, setTouchedFields] = useState<TouchedPersonalInfoFields>({});
  const step = useAudioChatStore((state) => state.step);
  const setStep = useAudioChatStore((state) => state.setStep);
  const chatTextData = useAudioChatStore((state) => state.chatTextData);
  const personalInfo = useAudioChatStore((state) => state.personalInfo);
  const updatePersonalInfo = useAudioChatStore((state) => state.updatePersonalInfo);
  const setChatTextData = useAudioChatStore((state) => state.setChatTextData);

  const personalInfoFields: PersonalInfoFieldConfig[] = [
    {
      field: "name",
      label: t`Full name`,
      placeholder: t`Alex Candidate`,
      type: "text",
      autoComplete: "name",
      isRequired: true,
    },
    {
      field: "phone",
      label: t`Phone`,
      placeholder: t`0400 000 000`,
      type: "tel",
      autoComplete: "tel",
      isRequired: true,
    },
    {
      field: "email",
      label: t`Email`,
      placeholder: t`alex@example.com`,
      type: "email",
      autoComplete: "email",
      isRequired: true,
    },
    {
      field: "linkedin",
      label: t`LinkedIn`,
      placeholder: t`linkedin.com/in/alex-candidate`,
      type: "url",
      autoComplete: "url",
    },
  ];

  const validatePersonalInfoField = (field: PersonalInfoField, value: string) => {
    const trimmed = value.trim();

    if (field === "name" && !trimmed) return t`Enter your full name.`;
    if (field === "phone" && !trimmed) return t`Enter your phone number.`;
    if (field === "email" && !trimmed) return t`Enter your email address.`;
    if (field === "email" && !emailRegex.test(trimmed)) return t`Enter a valid email address.`;
    if (field === "linkedin" && !isValidLinkedInUrl(trimmed)) {
      return t`Enter a valid LinkedIn URL.`;
    }

    return "";
  };

  const validatePersonalInfo = (values: personalInfoType) =>
    personalInfoFields.reduce<PersonalInfoErrors>((errors, { field }) => {
      const error = validatePersonalInfoField(field, values[field]);
      if (error) errors[field] = error;
      return errors;
    }, {});

  const pushMessages = useCallback((mes: Message) => {
    setMessages((prevMessages) => [...prevMessages, mes]);
    recordLegacyMessage(mes);
  }, []);

  const updateFieldValue = (field: PersonalInfoField, value: string) => {
    setFormValues((currentValues) => ({ ...currentValues, [field]: value }));

    if (touchedFields[field]) {
      const error = validatePersonalInfoField(field, value);
      setFormErrors((currentErrors) => ({ ...currentErrors, [field]: error || undefined }));
    }
  };

  const handleFieldBlur = (field: PersonalInfoField) => {
    setTouchedFields((currentFields) => ({ ...currentFields, [field]: true }));

    const error = validatePersonalInfoField(field, formValues[field]);
    setFormErrors((currentErrors) => ({ ...currentErrors, [field]: error || undefined }));
  };

  const submitPersonalInfo = (values: personalInfoType) => {
    const normalizedValues: personalInfoType = {
      name: values.name.trim(),
      phone: values.phone.trim(),
      email: values.email.trim(),
      linkedin: normalizeLinkedInUrl(values.linkedin),
    };

    const validationErrors = validatePersonalInfo(normalizedValues);
    if (Object.keys(validationErrors).length > 0) {
      setTouchedFields({
        name: true,
        phone: true,
        email: true,
        linkedin: true,
      });
      setFormErrors(validationErrors);
      return;
    }

    personalInfoFields.forEach(({ field }) => {
      updatePersonalInfo(field, normalizedValues[field]);
      recordLegacyStateUpdate(`basics.${field}`, normalizedValues[field]);
    });

    recordLegacyEvent({
      type: "prompt_submitted",
      promptId: personalInfoPromptId,
      value: normalizedValues,
    });

    const candidateName = normalizedValues.name;
    const firstName = getFirstName(candidateName);

    pushMessages({
      type: "user",
      content: t`Added contact details for ${candidateName}`,
    });
    pushMessages({
      type: "ai",
      content: t`Thanks, ${firstName}. I've saved your contact details.`,
    });

    setFormValues(normalizedValues);
    setIsFormSubmitted(true);
    setChatTextData("");
    setStep("WORK_EXP");
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitPersonalInfo(formValues);
  };

  const handleChatFallback = (input: string) => {
    const trimmedInput = input.trim();
    const nextEmptyField =
      personalInfoFields.find(({ field }) => !formValues[field].trim())?.field ?? "name";
    const nextValues = { ...formValues, [nextEmptyField]: trimmedInput };
    const fieldLabel =
      personalInfoFields.find(({ field }) => field === nextEmptyField)?.label ?? t`this field`;
    const lowerFieldLabel = fieldLabel.toLowerCase();
    const error = validatePersonalInfoField(nextEmptyField, trimmedInput);

    setFormValues(nextValues);
    setTouchedFields((currentFields) => ({ ...currentFields, [nextEmptyField]: true }));
    setFormErrors((currentErrors) => ({
      ...currentErrors,
      [nextEmptyField]: error || undefined,
    }));

    pushMessages({
      type: "user",
      content: trimmedInput,
    });
    pushMessages({
      type: "ai",
      content: error
        ? t`${fieldLabel} needs a quick fix. Please update it below.`
        : t`I added that to ${lowerFieldLabel}. You can finish the rest below.`,
    });

    setChatTextData("");
  };

  useEffect(() => {
    if (step === "PER_INFO") {
      const initialMessages: messageType = [
        {
          type: "ai",
          content: t`Add your contact details below. These are the details recruiters will see first.`,
        },
      ];
      setShowPerInfo(true);
      setIsFormSubmitted(false);
      setFormValues({
        ...emptyPersonalInfo,
        ...personalInfo,
      });
      setFormErrors({});
      setTouchedFields({});
      setMessages(initialMessages);
      recordLegacyMessages(initialMessages);
      recordLegacyEvent({ type: "prompt_shown", promptId: personalInfoPromptId });
    }
  }, [step]);

  useEffect(() => {
    if (step === "PER_INFO" && chatTextData) {
      handleChatFallback(chatTextData);
    }
  }, [chatTextData]);

  return (
    <>
      {showPerInfo &&
        messages.map((message, index) => (
          <div key={`per-${index}`}>
            <div
              className={cn(
                "mx-1 mb-3 max-w-[80%] rounded-lg px-4 py-2 text-base shadow-md",
                message.type === "user"
                  ? "ml-auto bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-800",
              )}
            >
              {message.type === "ai" ? <TypingText message={message.content} /> : message.content}
            </div>
          </div>
        ))}

      {showPerInfo && step === "PER_INFO" && !isFormSubmitted && (
        <form
          noValidate
          onSubmit={handleFormSubmit}
          className="mx-1 mb-5 w-[min(100%,46rem)] rounded-lg border border-border bg-background p-4 shadow-sm"
        >
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground">{t`Contact details`}</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t`Keep it accurate. You can skip LinkedIn if you do not use it.`}
              </p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {personalInfoFields.map(
              ({ field, label, placeholder, type, autoComplete, isRequired }) => {
                const error = formErrors[field];
                const errorId = `personal-info-${field}-error`;

                return (
                  <div key={field} className="min-w-0 space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <Label
                        htmlFor={`personal-info-${field}`}
                        className="text-xs font-medium text-foreground"
                      >
                        {label}
                      </Label>
                      {!isRequired && (
                        <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                          {t`Optional`}
                        </span>
                      )}
                    </div>
                    <Input
                      id={`personal-info-${field}`}
                      type={type}
                      value={formValues[field]}
                      autoComplete={autoComplete}
                      placeholder={placeholder}
                      aria-invalid={Boolean(error)}
                      aria-describedby={error ? errorId : undefined}
                      hasError={Boolean(error)}
                      onBlur={() => handleFieldBlur(field)}
                      onChange={(event) => updateFieldValue(field, event.target.value)}
                      className={cn(
                        "!h-10 !rounded-md !border bg-background text-sm shadow-none focus-visible:ring-2 focus-visible:ring-primary/30",
                        error && "!border-error focus-visible:ring-error/30",
                      )}
                    />
                    {error && (
                      <p id={errorId} className="text-xs font-medium text-error">
                        {error}
                      </p>
                    )}
                  </div>
                );
              },
            )}
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t`This only updates your resume contact section.`}
            </p>
            <Button type="submit" size="md" className="gap-2">
              <CheckCircle2 size={16} aria-hidden="true" />
              {t`Continue`}
            </Button>
          </div>
        </form>
      )}
    </>
  );
};
